import { useState } from "react";
import useBaseTable from "../hooks/useBaseTable";
import DSCheckbox from "../../../DSCheckBox";
import DSTable from "../../DSTable";
import DSTableBody from "../../DSTableBody";
import DSTableHead from "../../DSTableHead";
import DSTableRow from "../../DSTableRow";
import DSTableCell from "../../DSTableCell";
import DSTablePagination from "../../DSTablePagination";
import DSTableContainer from "../../DSTableContainer";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ConfirmationModal from "../../../../../views/ConfirmationModal";
import DSCircularProgress from "../../../DSCircularProgress";
export interface Column<T> {
  field: keyof T;
  // Backend sort path when it is not the field itself (a nested document has
  // no `keyof T` to name it). Doubles as the column's identity.
  sortField?: string;
  label: string;
  size?: number;
  sortable?: boolean;
}

export const columnKey = <T,>(col: Column<T>): string =>
  col.sortField ?? (col.field as string);

export interface DSBaseTableProps<T> {
  data: T[];
  columns: Column<T>[];
  rowBuilder?: (row: T, columns: Column<T>[]) => JSX.Element;
  onPressRow?: (row: T) => void;
  selectedRows?: T[];
  onSelectRows?: (rows: T[]) => void;
  fetchMore?: () => void;
  onSelectSingleRow?: (row: T) => void;
  onPressDelete?: (row: T) => void;
  onSort?: (field: keyof T) => void;
  sortState?: Record<string, 1 | -1>;
  defaultRowsPerPage?: number;
  deletingRowId?: string | null;
  getRowId?: (row: T) => string;
  /**
   * When false, clicking the delete icon calls `onPressDelete` directly
   * instead of showing the built-in "Are you sure?" confirmation — use this
   * when the consumer renders its own confirmation/options dialog. Defaults
   * to true.
   */
  confirmDelete?: boolean;
}

const DSBaseTable = <T,>({
  data,
  columns,
  rowBuilder,
  onPressRow,
  selectedRows,
  onSelectRows,
  fetchMore,
  onSelectSingleRow,
  onPressDelete,
  onSort,
  sortState,
  defaultRowsPerPage,
  deletingRowId,
  getRowId,
  confirmDelete = true,
}: DSBaseTableProps<T>) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<T | null>(null);

  const handleDeleteClick = (row: T, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!confirmDelete) {
      onPressDelete?.(row);
      return;
    }
    setRowToDelete(row);
    setConfirmationOpen(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete && onPressDelete) {
      onPressDelete(rowToDelete);
    }
    setConfirmationOpen(false);
    setRowToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmationOpen(false);
    setRowToDelete(null);
  };
  const {
    defaultWidth,
    paginatedData,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useBaseTable<T>({
    data,
    columns,
    onPressRow,
    fetchMore,
    defaultRowsPerPage,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <DSTableContainer sx={{ flex: 1, overflow: "auto" }}>
        <DSTable stickyHeader>
          <DSTableHead>
            <DSTableRow>
              {onSelectRows && (
                <DSTableCell
                  key={"selectAll"}
                  padding="checkbox"
                  sx={{ width: 50 }}
                >
                  <DSCheckbox
                    checked={selectedRows?.length === data.length}
                    onChange={() => {
                      onSelectRows(data);
                    }}
                  />
                </DSTableCell>
              )}
              {columns.map((col) => (
                <DSTableCell
                  key={columnKey(col)}
                  sx={{
                    width: col.size || `${defaultWidth}%`,
                    whiteSpace: "nowrap",
                    cursor: col.sortable && onSort ? "pointer" : "default",
                    userSelect: "none",
                    "&:hover":
                      col.sortable && onSort
                        ? {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          }
                        : {},
                  }}
                  onClick={() => {
                    if (col.sortable && onSort) {
                      // May be a nested path; such handlers take `string`.
                      onSort(columnKey(col) as keyof T);
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {col.label}
                    {col.sortable && (
                      <>
                        {sortState?.[columnKey(col)] === 1 ? (
                          <ArrowUpwardIcon fontSize="small" color="primary" />
                        ) : sortState?.[columnKey(col)] === -1 ? (
                          <ArrowDownwardIcon fontSize="small" color="primary" />
                        ) : (
                          <SwapVertIcon
                            fontSize="small"
                            sx={{
                              opacity: 0.3,
                              "&:hover": { opacity: 0.7 },
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </DSTableCell>
              ))}
              {onPressDelete && (
                <DSTableCell
                  key="delete"
                  sx={{
                    width: 50,
                    whiteSpace: "nowrap",
                  }}
                ></DSTableCell>
              )}
            </DSTableRow>
          </DSTableHead>
          <DSTableBody>
            {paginatedData.map((row, index) => (
              <DSTableRow key={index} hover onClick={() => onPressRow?.(row)}>
                {onSelectSingleRow && (
                  <DSTableCell padding="checkbox" sx={{ width: 50 }}>
                    <DSCheckbox
                      checked={selectedRows?.includes(row)}
                      onChange={() => onSelectSingleRow(row)}
                    />
                  </DSTableCell>
                )}
                {rowBuilder
                  ? rowBuilder(row, columns)
                  : columns.map((col) => (
                      <DSTableCell
                        key={columnKey(col)}
                        onClick={() => {
                          if (onPressRow) {
                            onPressRow(row);
                          }
                        }}
                        sx={{
                          width: col.size || `${defaultWidth}%`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {String(row[col.field])}
                      </DSTableCell>
                    ))}
                {onPressDelete &&
                  (() => {
                    const rowId = getRowId?.(row);
                    const isDeleting = rowId != null && rowId === deletingRowId;
                    return (
                      <DSTableCell
                        key="delete"
                        padding="checkbox"
                        sx={{
                          width: 50,
                          whiteSpace: "nowrap",
                          cursor: isDeleting ? "default" : "pointer",
                        }}
                        onClick={(e) =>
                          !isDeleting && handleDeleteClick(row, e)
                        }
                      >
                        {isDeleting ? (
                          <DSCircularProgress size={20} />
                        ) : (
                          <DeleteIcon color="error" fontSize="small" />
                        )}
                      </DSTableCell>
                    );
                  })()}
              </DSTableRow>
            ))}
          </DSTableBody>
        </DSTable>
      </DSTableContainer>
      <DSTablePagination
        count={fetchMore ? -1 : data.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: "divider",
        }}
      />
      <ConfirmationModal
        open={confirmationOpen}
        message="Are you sure you want to delete this item?"
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default DSBaseTable;
